import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Heart, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useFavorites } from "../context/FavoritesContext";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { resolveImageUrl } from "../services/catalogService";
import { FEATURES } from "../config/features";

export default function FavoritesPage() {
  const fav = useFavorites();
  const cart = useCart();
  const { t } = useLanguage();

  // Hidden from the public site per client decision (config/features.js)
  if (!FEATURES.favorites) return <Navigate to="/" replace />;

  const moveToRequest = (item) => {
    cart.addLine(item);
    toast.success(t("actions.added"), { description: item.name });
  };

  return (
    <main className="min-h-screen bg-[#F6F2EC] text-[#252525] pt-28 pb-24" data-testid="favorites-page">
      <div className="container-tessa">
        <Link to="/" className="flex w-fit items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#817B74] hover:text-[#D92A47] transition-colors mb-8">
          <ArrowLeft size={14} /> {t("pages.backHome")}
        </Link>
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">{t("pages.favorites.eyebrow")}</span>
            <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] text-[#252525] mt-3">{t("pages.favorites.title")}</h1>
            <p className="text-[#817B74] mt-3">{t("pages.favorites.subtitle")}</p>
          </div>
          {fav.items.length > 0 && (
            <button
              onClick={() => { fav.clear(); toast(t("pages.favorites.cleared")); }}
              data-testid="clear-favorites"
              className="text-[12px] uppercase tracking-[0.14em] text-[#817B74] hover:text-[#D92A47]"
            >
              {t("pages.favorites.clearAll")}
            </button>
          )}
        </div>

        {fav.items.length === 0 ? (
          <div className="border border-black/10 bg-white p-12 text-center" data-testid="favorites-empty">
            <Heart size={28} strokeWidth={1.4} className="text-[#D92A47] mx-auto mb-4" />
            <p className="font-serif text-2xl text-[#252525]">{t("pages.favorites.emptyTitle")}</p>
            <p className="text-[#817B74] mt-2">{t("pages.favorites.emptyText")}</p>
            <Link to="/" className="inline-block mt-6 text-[12px] uppercase tracking-[0.14em] text-[#D92A47] border-b border-[#D92A47]">
              {t("pages.variety.browse")}
            </Link>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-list">
            {fav.items.map((item) => (
              <li key={item.id} className="bg-white border border-black/10 flex flex-col">
                <Link to={`/variety/${item.id}`} className="block aspect-[4/3] overflow-hidden bg-[#F6F2EC]" data-testid={`fav-view-${item.id}`}>
                  {item.image && <img src={resolveImageUrl(item.image)} alt={item.name} className="h-full w-full object-cover hover:scale-[1.03] transition-transform duration-500" />}
                </Link>
                <div className="p-5 flex-1 flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">{item.category_label}</span>
                  <h3 className="font-serif text-2xl text-[#252525] mt-1">
                    <Link to={`/variety/${item.id}`} className="hover:text-[#D92A47] transition-colors">{item.name}</Link>
                  </h3>
                  <div className="flex items-center gap-2 mt-auto pt-6">
                    <button
                      onClick={() => moveToRequest(item)}
                      data-testid={`fav-to-order-${item.id}`}
                      className="inline-flex items-center gap-2 h-10 px-4 bg-[#D92A47] text-[#F6F2EC] text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] transition-colors"
                    >
                      <Plus size={14} /> {t("actions.add")}
                    </button>
                    <button
                      onClick={() => fav.remove(item.id)}
                      data-testid={`fav-remove-${item.id}`}
                      className="grid place-items-center h-10 w-10 border border-black/10 hover:text-[#D92A47] hover:border-[#D92A47] transition-colors"
                      aria-label={t("pages.favorites.remove")}
                    >
                      <Trash2 size={15} strokeWidth={1.6} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
